import { useCallback, useEffect, useState } from 'react';
import type { SessionInfo } from '@authsemply/shared';
import { api, ApiError } from '../lib/api';
import { TITRE_VERROU_BETA } from './CompteBetaAvis';

const dateHeure = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });

/** Libellé lisible d'un user-agent : « Chrome sur Windows », sinon le texte tronqué. */
export function libelleAppareil(userAgent: string | null | undefined): string {
  if (!userAgent) return 'Appareil inconnu';
  const navigateur = /Edg\//.test(userAgent)
    ? 'Edge'
    : /OPR\/|Opera/.test(userAgent)
      ? 'Opera'
      : /Firefox\//.test(userAgent)
        ? 'Firefox'
        : /Chrome\//.test(userAgent)
          ? 'Chrome'
          : /Safari\//.test(userAgent)
            ? 'Safari'
            : null;
  const systeme = /Windows/.test(userAgent)
    ? 'Windows'
    : /Android/.test(userAgent)
      ? 'Android'
      : /iPhone|iPad|iOS/.test(userAgent)
        ? 'iOS'
        : /Mac OS X|Macintosh/.test(userAgent)
          ? 'macOS'
          : /Linux/.test(userAgent)
            ? 'Linux'
            : null;
  if (navigateur && systeme) return `${navigateur} sur ${systeme}`;
  return navigateur ?? systeme ?? userAgent.slice(0, 60);
}

/**
 * « Mes sessions » de Mon compte (recette AUTH, 05/10/2026) : les appareils
 * actuellement connectés, la session en cours marquée, et de quoi fermer
 * chacune des autres (`GET` / `DELETE /auth/sessions`). Le serveur ne ferme que
 * les sessions de la personne connectée.
 */
export function SessionsSection({ verrouille = false }: { verrouille?: boolean } = {}) {
  const [sessions, setSessions] = useState<SessionInfo[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState<string | null>(null);

  const charger = useCallback(() => {
    api
      .get<SessionInfo[]>('/auth/sessions')
      .then(setSessions)
      .catch(() => setSessions(null));
  }, []);

  useEffect(charger, [charger]);

  async function fermer(session: SessionInfo) {
    if (!window.confirm('Fermer cette session ? L’appareil concerné devra se reconnecter.')) return;
    setOccupe(session.id);
    setErreur(null);
    try {
      await api.delete(`/auth/sessions/${encodeURIComponent(session.id)}`);
      charger();
    } catch (err) {
      setErreur(err instanceof ApiError ? err.message : 'Fermeture impossible pour le moment.');
    } finally {
      setOccupe(null);
    }
  }

  if (!sessions || sessions.length === 0) return null;

  return (
    <>
      <h2 style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>Mes sessions</h2>
      {erreur ? <div className="alert alert-error">{erreur}</div> : null}
      <ul
        aria-label="Sessions ouvertes"
        style={{ listStyle: 'none', padding: 0, margin: '0 0 1.5rem', fontSize: '0.9rem' }}
      >
        {sessions.map((s) => (
          <li
            key={s.id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem',
              padding: '0.6rem 0',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            <span>
              <strong>{libelleAppareil(s.userAgent)}</strong>
              {s.current ? ' — cette session' : ''}
              <div style={{ color: 'var(--text-color-light)', fontSize: '0.8rem' }}>
                Ouverte le {dateHeure(s.createdAt)}, dernière activité le {dateHeure(s.lastSeenAt)}
              </div>
            </span>
            {s.current ? null : (
              <button
                className="btn btn-secondary"
                style={{ width: 'auto', padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                type="button"
                disabled={occupe !== null || verrouille}
                title={verrouille ? TITRE_VERROU_BETA : undefined}
                onClick={() => void fermer(s)}
              >
                Fermer
              </button>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}
